import React, { useCallback, useEffect, useState } from "react";

import { getAdminModule, updateModule } from "services";

import { incomplete, useCancelAsyncOnUnmount, useNavigate, useParams } from "utils";

import { icons } from "enums";

import { AdminHead, Col, ErrorBoundary, Form, MainPage, Row, Tabs, Message as message } from "components";

import { CropSwap } from "pages/admin/programs/components/CropSwap";
import { ModuleCropRecommendationTab } from "pages/admin/programs/components/ModuleCropRecommendationTab";
import { ModuleDetails } from "pages/admin/programs/components/ModuleDetails";
import { ModuleProperties } from "pages/admin/programs/components/ModuleProperties";
import { ModuleQuotes } from "pages/admin/programs/components/ModuleQuotes";

export const ModuleUpdate = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [imageSuccess, setImageSuccess] = useState(false);
  const [imageURL, setImageURL] = useState(false);
  const [module, setModule] = useState(false);
  const [description, setDescription] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectorDescription, setSelectorDescription] = useState(false);
  const [farmBenefits, setFarmBenefits] = useState([]);
  const [postSuggestion1, setPostSuggestion1] = useState("");
  const [postSuggestion2, setPostSuggestion2] = useState("");
  const [measures, setMeasures] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const getModule = useCallback(() => {
    // setLoading(true);

    getAdminModule({
      id: id,
      signal,
    })
      .then((res) => {
        setModule(res);
        setDescription(res.longDescription);
        setImageURL(res.image);
        setImageSuccess(!!res.image);
        setQuotes(res.moduleQuotes);
        setFarmBenefits(res.farmBenefits);
        setPostSuggestion1(res.postSuggestion1);
        setPostSuggestion2(res.postSuggestion2);
        setMeasures(res.measures);
        setSelectorDescription(res.selectorDescription);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, signal]);

  const complete = (v) => {
    setLoading(true);

    updateModule({
      ...v,
      ...{
        id: id,
        longDescription: description,
        selectorDescription: selectorDescription,
        farmBenefits: farmBenefits,
        image: imageURL,
        quotes: quotes,
        postSuggestion1: postSuggestion1,
        postSuggestion2: postSuggestion2,
        measures: measures,
      },
    })
      .then(() => {
        navigate("/programs/modules");
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updatePostSuggestion = (e, suggestion) => {
    if (suggestion === "postSuggestion1") {
      setPostSuggestion1(e.target.value);
    } else {
      setPostSuggestion2(e.target.value);
    }
  };

  const updateContent = (item, v) => {
    const content = v.replace(/height="\d*"/g, 'height="auto" style="max-width: 100%;"');

    if (item === "description") {
      return setDescription(content);
    }

    if (item === "measures") {
      return setMeasures(content);
    }

    if (item === "dataToProvide") {
      return setSelectorDescription(content);
    }
  };

  const updateData = () => {
    getModule();
  };

  const addFarmBenefit = (v) => {
    if (v.target.value !== "") {
      const arr = farmBenefits || [];
      arr.push(v.target.value);
      setFarmBenefits([...arr]);
    }
  };

  const deleteFarmBenefit = (i) => {
    const arr = farmBenefits || [];
    arr.splice(i, 1);
    setFarmBenefits([...arr]);
  };

  useEffect(() => {
    getModule();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moduleIcons = icons.sort().map((item) => item);

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline="Update a module"
        link="/programs/modules"
        tooltipText="Back to modules"
        description={<>Update a module here. Browse the module tabs to update specific lists of properties.</>}
      />
      {module && (
        <Row>
          <Col>
            <Form name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
              <Row>
                <Col>
                  <Tabs
                    size="small"
                    items={[
                      {
                        label: "General",
                        key: "general",
                        children: (
                          <ErrorBoundary>
                            <ModuleDetails
                              addFarmBenefit={addFarmBenefit}
                              deleteFarmBenefit={deleteFarmBenefit}
                              description={description}
                              farmBenefits={farmBenefits}
                              imageSuccess={imageSuccess}
                              imageURL={imageURL}
                              icons={moduleIcons}
                              module={module}
                              setImageSuccess={setImageSuccess}
                              setImageURL={setImageURL}
                              setLoading={setLoading}
                              updateContent={updateContent}
                              updatePostSuggestion={updatePostSuggestion}
                            />
                          </ErrorBoundary>
                        ),
                        className: "midi-form",
                      },
                      ...module.lists.map((list) => ({
                        label: list.name,
                        key: list.type,
                        children: (
                          <ErrorBoundary>
                            <ModuleProperties list={list} module={module} updateData={updateData} />
                          </ErrorBoundary>
                        ),
                      })),
                      module.module === "crops" && {
                        label: "Crop Recommendation",
                        key: "cropRecommendation",
                        children: (
                          <ErrorBoundary>
                            <ModuleCropRecommendationTab module={module} />
                          </ErrorBoundary>
                        ),
                      },
                      {
                        label: "Quotes",
                        key: "quotes",
                        children: (
                          <ErrorBoundary>
                            <ModuleQuotes module={module} quotes={quotes} setQuotes={setQuotes} />
                          </ErrorBoundary>
                        ),
                        className: "midi-form",
                      },
                      {
                        label: "Crop swap",
                        key: "swap",
                        children: (
                          <ErrorBoundary>
                            <CropSwap />
                          </ErrorBoundary>
                        ),
                        className: "midi-form",
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      )}
    </MainPage>
  );
};
